import { supabase } from "@/lib/supabase";
import { jobColumnFor } from "@/lib/jobIdentifier";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import { JobService } from "./job.service";
import { SystemUpdateService } from "@/services/system-update.service";
import type { BuildReviewEvent, BuildReviewAction, RevisionReason } from "@/data/buildReviewTypes";
import { REVISION_REASON_LABELS } from "@/data/buildReviewTypes";
import type { Job } from "@/data/types";
import { toUiBuildReviewEvent } from "@/mappers/build-review.mapper";
import { generateMaterialList, toMaterialLineItems } from "@/data/skuMapping";
import { replaceJobMaterialsSnapshot } from "@/services/inventory.service";
import { ensureProductsForMaterialNames, getProducts as getRegistryProducts } from "@/services/productRegistry.service";
import { isExternalManagedImport } from "@/data/import/migrationVisibilityGuard";

// ── DB row shape — matches build_review_events table ─────────────────────────
export interface BuildReviewEventRow {
  id: string;                          // uuid (primary key)
  job_id: string;                      // uuid FK to jobs.id
  job_number: string | null;           // denormalized for display
  action: BuildReviewAction;
  timestamp: string;
  performed_by: string;
  notes: string | null;
  revision_notes: string | null;
  revision_reason: RevisionReason | null;
  material_changes: string | null;
  created_at: string;
}

function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim());
}

// ── Resolve job reference (UUID/job_number) → UUID ───────────────────────────
async function resolveJobUuid(jobRef: string): Promise<string | null> {
  const normalized = String(jobRef || "").trim();
  if (!normalized) return null;
  if (isUuid(normalized)) return normalized;
  const { data, error } = await supabase
    .from("jobs")
    .select("id")
    .eq(jobColumnFor(normalized), normalized)
    .limit(1);

  if (error) {
    console.warn("[BuildReviewService.resolveJobUuid] query error:", error.message);
    return null;
  }

  // Handle both array (from .limit(1)) and object (from .maybeSingle fallback) cases
  // This handles the case where duplicate job_numbers might exist in the database
  if (Array.isArray(data)) {
    if (data.length > 1) {
      console.warn(`[BuildReviewService.resolveJobUuid] Multiple jobs found for ${jobColumnFor(normalized)}=${normalized}, using first match.`);
    }
    return data[0]?.id ?? null;
  }

  return (data as { id?: string } | null)?.id ?? null;
}

function normalizeLookupKey(value: string | null | undefined): string {
  return (value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, " ")
    .replace(/[^\w\s]/g, "");
}

type OrderingCandidate = { name: string; quantity: number; skuHint?: string; unitCost?: number; unitPrice?: number };
type BundleScopeKey = "bathroom" | "windows" | "doors" | "flooring" | "default";

function resolveProjectBundleKey(job: Job): BundleScopeKey {
  const rawProjectType = [
    String(job.projectType || ""),
    String(job.projectConfig?.autoProjectType || ""),
    String(job.projectConfig?.bathroomSystemType || ""),
    String(job.projectConfig?.primaryMaterial || ""),
  ]
    .join(" ")
    .toLowerCase();
  if (rawProjectType.includes("window")) return "windows";
  if (rawProjectType.includes("door")) return "doors";
  if (rawProjectType.includes("floor")) return "flooring";
  if (
    rawProjectType.includes("bath") ||
    rawProjectType.includes("shower") ||
    rawProjectType.includes("tub")
  ) {
    return "bathroom";
  }
  return "default";
}

function parseAutoBundleScopes(notes: string): Map<BundleScopeKey, number> {
  const scopes = new Map<BundleScopeKey, number>();
  const raw = String(notes || "");
  const match = raw.match(/AUTO_BUNDLE:([^\n\r]+)/i);
  if (!match) return scopes;
  const body = match[1].toLowerCase();
  const knownScopes: BundleScopeKey[] = ["bathroom", "windows", "doors", "flooring", "default"];
  for (const scope of knownScopes) {
    const qtyMatch = body.match(new RegExp(`\\b${scope}\\b\\s*(?:=|:)\\s*(\\d+)`, "i"));
    if (qtyMatch) {
      scopes.set(scope, Math.max(1, Number(qtyMatch[1] || 1) || 1));
      continue;
    }
    if (new RegExp(`\\b${scope}\\b`, "i").test(body)) {
      scopes.set(scope, 1);
    }
  }
  return scopes;
}

function getRegistryBundleCandidates(
  registryProducts: Awaited<ReturnType<typeof getRegistryProducts>>,
  projectBundleKey: BundleScopeKey,
): OrderingCandidate[] {
  return registryProducts
    .filter((product) => product.active)
    .flatMap((product) => {
      const scopes = parseAutoBundleScopes(product.notes || "");
      if (!scopes.has(projectBundleKey) && !scopes.has("default")) return [];
      const qty = Math.max(1, scopes.get(projectBundleKey) ?? scopes.get("default") ?? 1);
      return [{
        name: product.productName,
        quantity: qty,
        skuHint: product.sku,
        unitCost: product.cost,
        unitPrice: product.price,
      }];
    });
}

function toProjectConfigOrderingCandidates(job: Job): OrderingCandidate[] {
  const cfg = job.projectConfig;
  if (!cfg) return [];

  const out: OrderingCandidate[] = [];
  const looksLikeUuid = (value: string) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim());
  const add = (name: string | null | undefined, skuHint?: string, quantity = 1) => {
    const value = (name || "").trim();
    if (!value) return;
    // Ignore raw UUID option ids leaking from config; they are not human material names.
    if (looksLikeUuid(value)) return;
    out.push({ name: value, skuHint, quantity });
  };

  const wallSystem = (cfg.selectedWallSystem || cfg.primaryMaterial || cfg.bathroomMaterials?.[0] || "").trim();
  add(wallSystem, "WAL-001");
  add(cfg.selectedBase || cfg.baseType, "BAS-001");
  add(cfg.enclosureType || cfg.selectedGlass, "ENC-001");
  add(cfg.selectedFixture, "FIX-001");
  (cfg.selectedGrabBars || []).forEach((bar) => add(bar, "HAR-001"));
  (cfg.selectedAddOns || []).forEach((addOn) => add(addOn, "ADD-001"));
  (cfg.customAddOns || []).forEach((custom) => add(custom.name, "CUS-001"));

  if (cfg.plumbingRelocation) add("Plumbing relocation required", "PRE-001");
  if (cfg.customModifications) add("Custom modifications included", "CUS-001");

  const merged = new Map<string, OrderingCandidate>();
  out.forEach((item) => {
    const key = normalizeLookupKey(item.name);
    const existing = merged.get(key);
    if (!existing) {
      merged.set(key, { ...item });
      return;
    }
    merged.set(key, {
      name: existing.name || item.name,
      skuHint: existing.skuHint || item.skuHint,
      quantity: Math.max(existing.quantity, item.quantity),
    });
  });
  return Array.from(merged.values());
}

async function buildMergedMaterialsList(
  job: Job,
  includeConfigFallback: boolean = false,
): Promise<NonNullable<Job["materialsList"]>> {
  const byKey = new Map<string, NonNullable<Job["materialsList"]>[number]>();
  // Secondary index: normalizedName → primary key in byKey.
  // Used to prevent config-fallback candidates from creating duplicate rows
  // when the same material already exists under a longer catalog name or different SKU.
  const byNameKey = new Map<string, string>();
  let selectedBundleNameKeys = new Set<string>();
  let bundleUniverseNameKeys = new Set<string>();

  // Returns true if a SKU looks like a proper catalog code (e.g. "WALL-SEN-STD")
  // vs a compact placeholder produced by normalizeSkuToken (e.g. "WAL001").
  const isCatalogSku = (s: string) => !!s && s !== "—" && /[A-Z0-9]{2,}-[A-Z0-9]{2,}/.test(s);

  const addLine = (
    line: Partial<NonNullable<Job["materialsList"]>[number]> & { itemName: string; sku?: string },
  ) => {
    const itemName = (line.itemName || "").trim();
    const sku = (line.sku || "").trim();
    if (!itemName && !sku) return;
    // Prefer SKU identity when available to avoid duplicate rows for same SKU with different labels.
    const skuKey = normalizeLookupKey(sku);
    const nameKey = normalizeLookupKey(itemName);
    const key = skuKey ? `sku:${skuKey}` : `name:${nameKey}`;

    // Primary: exact key match
    let existing = byKey.get(key);

    // Secondary: name-based dedup.
    // Config-fallback candidates (e.g. name="Sentrel", sku="WAL001") must NOT create a
    // second row when a catalog entry already covers that material (e.g. name="Sentrel
    // Standard Wall Panel Set", sku="WALL-SEN-STD").  We check for exact name match
    // first, then for prefix overlap ("sentrel" is a prefix of "sentrel standard …").
    if (!existing && nameKey) {
      const directMatch = byNameKey.get(nameKey);
      if (directMatch) {
        existing = byKey.get(directMatch);
      } else {
        for (const [regName, regKey] of byNameKey.entries()) {
          if (regName.startsWith(nameKey) || nameKey.startsWith(regName)) {
            existing = byKey.get(regKey);
            break;
          }
        }
      }
    }

    if (!existing) {
      const entry: NonNullable<Job["materialsList"]>[number] = {
        sku: sku || "—",
        itemName: itemName || sku || "Unspecified Item",
        quantityRequired: Math.max(1, Number(line.quantityRequired || 1)),
        quantityAllocated: Number(line.quantityAllocated || 0),
        quantityStaged: Number(line.quantityStaged || 0),
        quantityInstalled: Number(line.quantityInstalled || 0),
        missingItems: Number(line.missingItems ?? Math.max(0, Number(line.quantityRequired || 1))),
        unitCost: Number(line.unitCost || 0),
        fieldPurchase: !!line.fieldPurchase,
        inventoryStatus: line.inventoryStatus || "In Stock",
        inventoryAssignment: line.inventoryAssignment || "Stock",
        lifecycleStage: line.lifecycleStage || "Ordered",
      };
      byKey.set(key, entry);
      if (nameKey) byNameKey.set(nameKey, key);
      return;
    }

    existing.quantityRequired = Math.max(existing.quantityRequired, Number(line.quantityRequired || 1));
    // Prefer catalog SKUs over placeholders. A catalog SKU has internal hyphens
    // (e.g. "WALL-SEN-STD"); a placeholder is compact alphanumeric (e.g. "WAL001").
    const existingIsCatalog = isCatalogSku(existing.sku);
    const newIsCatalog = isCatalogSku(sku);
    if (!existingIsCatalog && newIsCatalog) {
      existing.sku = sku;
    } else if (!existing.sku || existing.sku === "—") {
      existing.sku = sku || existing.sku;
    }
    if (existing.unitCost <= 0) existing.unitCost = Number(line.unitCost || 0);
    if (line.inventoryAssignment === "Auto Bundle") existing.inventoryAssignment = "Auto Bundle";
  };

  const existingMaterials = job.materialsList || [];
  const hasAuthoritativeQuoteMaterials = existingMaterials.length > 0;
  existingMaterials.forEach((line) => addLine(line));

  // Only synthesize from mapping/config when no authoritative materials list exists yet.
  if ((includeConfigFallback || !hasAuthoritativeQuoteMaterials) && job.projectConfig) {
    const generated = toMaterialLineItems(generateMaterialList(job.projectConfig));
    generated.forEach((line) => addLine(line));
  }
  const baseCandidates = ((includeConfigFallback || !hasAuthoritativeQuoteMaterials) && job.projectConfig)
    ? toProjectConfigOrderingCandidates(job)
    : [];
  const projectBundleKey = resolveProjectBundleKey(job);
  try {
    const registryProducts = await getRegistryProducts();
    // Registry-tagged names form the pruning universe.
    bundleUniverseNameKeys = new Set<string>();
    registryProducts.forEach((product) => {
      const scopes = parseAutoBundleScopes(product.notes || "");
      if (scopes.size > 0) {
        bundleUniverseNameKeys.add(normalizeLookupKey(product.productName));
      }
    });
    const registryBundleCandidates = getRegistryBundleCandidates(registryProducts, projectBundleKey);
    selectedBundleNameKeys = new Set(registryBundleCandidates.map((candidate) => normalizeLookupKey(candidate.name)));
    const candidates = [...baseCandidates, ...registryBundleCandidates];
    const initialSkuByName = new Map(
      registryProducts.map((product) => [normalizeLookupKey(product.productName), product.sku]),
    );
    let skuByName = initialSkuByName;
    try {
      await ensureProductsForMaterialNames(
        candidates.map((candidate) => ({
          name: candidate.name,
          assignedSku: candidate.skuHint,
        })),
      );
      const refreshedRegistryProducts = await getRegistryProducts();
      skuByName = new Map(
        refreshedRegistryProducts.map((product) => [normalizeLookupKey(product.productName), product.sku]),
      );
    } catch (ensureError) {
      // Do not drop DB-selected bundle items if ensure/write path fails.
      // Keep using current registry read results for SKU resolution.
      console.warn("[BuildReviewService] ensureProductsForMaterialNames failed; continuing with existing registry map:", ensureError);
    }
    candidates.forEach((candidate) => {
      const isAutoBundle = selectedBundleNameKeys.has(normalizeLookupKey(candidate.name));
      const resolvedSku = skuByName.get(normalizeLookupKey(candidate.name)) || candidate.skuHint;
      addLine({
        sku: resolvedSku,
        itemName: candidate.name,
        quantityRequired: candidate.quantity,
        unitCost: candidate.unitCost,
        unitPrice: candidate.unitPrice,
        inventoryAssignment: isAutoBundle ? "Auto Bundle" : undefined,
      });
    });
  } catch (error) {
    console.warn("[BuildReviewService] Failed to auto-map candidate materials:", error);
    selectedBundleNameKeys = new Set<string>();
    bundleUniverseNameKeys = new Set<string>();
    baseCandidates.forEach((candidate) => {
      addLine({
        sku: candidate.skuHint,
        itemName: candidate.name,
        quantityRequired: candidate.quantity,
      });
    });
  }

  // Prune stale auto-bundle rows that were previously injected but are no longer
  // selected for the active project-type bundle configuration.
  if (bundleUniverseNameKeys.size > 0) {
    for (const [key] of byKey.entries()) {
      if (bundleUniverseNameKeys.has(key) && !selectedBundleNameKeys.has(key)) {
        byKey.delete(key);
      }
    }
  }

  return Array.from(byKey.values());
}

function resetReviewMaterialState(
  rows: NonNullable<Job["materialsList"]>,
): NonNullable<Job["materialsList"]> {
  return rows.map((row) => {
    const required = Math.max(1, Number(row.quantityRequired ?? 1));
    return {
      ...row,
      quantityRequired: required,
      quantityAllocated: 0,
      quantityStaged: 0,
      quantityInstalled: 0,
      missingItems: required,
      inventoryAssignment: "Stock",
      lifecycleStage: "Ordered",
      inventoryStatus: "In Stock",
    };
  });
}

// ── Service ───────────────────────────────────────────────────────────────────
export const BuildReviewService = {
  /**
   * Build a live merged materials preview for Build Review UI.
   * Uses the same pipeline as approval/final-approval, including
   * Product Registry-driven project-type auto bundle resolution.
   */
  async previewMergedMaterials(job: Job): Promise<NonNullable<Job["materialsList"]>> {
    if (isExternalManagedImport(job)) {
      return Array.isArray(job.materialsList) ? job.materialsList : [];
    }
    return buildMergedMaterialsList(job);
  },

  /**
   * Rebuilds job materials from current job data + project config fallbacks
   * without mutating workflow stage/status flags.
   */
  async rebuildMaterials(
    jobNumber: string,
  ): Promise<{ data: Job | null; error: unknown }> {
    const currentJobResult = await JobService.getById(jobNumber);
    if (currentJobResult.error || !currentJobResult.data) {
      return { data: null, error: currentJobResult.error || new Error(`Job not found: ${jobNumber}`) };
    }
    if (isExternalManagedImport(currentJobResult.data)) {
      return {
        data: currentJobResult.data,
        error: new Error("External-managed import: material rebuild fallback is disabled"),
      };
    }
    const rebuiltMaterials = resetReviewMaterialState(
      await buildMergedMaterialsList(currentJobResult.data, true),
    );
    await replaceJobMaterialsSnapshot({
      ...currentJobResult.data,
      materialsList: rebuiltMaterials,
    });
    const updateResult = await JobService.update(jobNumber, {
      materialsList: rebuiltMaterials,
    });
    if (updateResult.data) {
      await replaceJobMaterialsSnapshot({
        ...updateResult.data,
        materialsList: rebuiltMaterials,
      });
    }
    return updateResult;
  },

  /** Fetch all build review events, ordered chronologically */
  async getAll(): Promise<{ data: BuildReviewEvent[] | null; error: unknown }> {
    const { data, error } = await supabase
      .from("build_review_events")
      .select("*")
      .order("timestamp", { ascending: true });

    if (error || !data) return { data: null, error };
    return { data: (data as BuildReviewEventRow[]).map(toUiBuildReviewEvent), error: null };
  },

  /** Fetch all events for a specific job (by job_number) */
  async getByJobNumber(
    jobNumber: string
  ): Promise<{ data: BuildReviewEvent[] | null; error: unknown }> {
    const jobUuid = await resolveJobUuid(jobNumber);
    if (!jobUuid) return { data: [], error: null };

    const { data, error } = await supabase
      .from("build_review_events")
      .select("*")
      .eq("job_id", jobUuid)
      .order("timestamp", { ascending: true });

    if (error || !data) return { data: null, error };
    return { data: (data as BuildReviewEventRow[]).map(toUiBuildReviewEvent), error: null };
  },

  /** Append a single event to the log */
  async addEvent(
    jobNumber: string,
    action: BuildReviewAction,
    performedBy: string,
    extra?: {
      notes?: string;
      revisionNotes?: string;
      revisionReason?: RevisionReason;
      materialChanges?: string;
    }
  ): Promise<{ data: BuildReviewEvent | null; error: unknown }> {
    const jobUuid = await resolveJobUuid(jobNumber);
    if (!jobUuid) return { data: null, error: new Error(`Job not found: ${jobNumber}`) };

    const row: Omit<BuildReviewEventRow, "id" | "created_at"> = {
      job_id: jobUuid,
      job_number: jobNumber,
      action,
      timestamp: new Date().toISOString(),
      performed_by: performedBy,
      notes: extra?.notes ?? null,
      revision_notes: extra?.revisionNotes ?? null,
      revision_reason: extra?.revisionReason ?? null,
      material_changes: extra?.materialChanges ?? null,
    };

    const { data, error } = await supabase
      .from("build_review_events")
      .insert([row])
      .select()
      .single();

    if (error) {
      // PGRST116: multiple rows returned (shouldn't happen with insert, but log if it does)
      if (error.code === "PGRST116") {
        console.warn("[BuildReviewService.addEvent] PGRST116 - multiple rows unexpectedly returned");
      } else {
        console.error("[BuildReviewService.addEvent] error:", JSON.stringify(error));
      }
      return { data: null, error };
    }
    return { data: toUiBuildReviewEvent(data as BuildReviewEventRow), error: null };
  },

  // ── High-level workflow actions ─────────────────────────────────────────────

  /**
   * Approve a job's build review.
   * Updates the job: scopeLocked, intakeApproved, approvedForOrdering, planningStatus,
   * currentStage → Review Complete.
   */
  async approve(
    jobNumber: string,
    performedBy: string,
    notes?: string
  ): Promise<{ data: Job | null; error: unknown }> {
    const currentJobResult = await JobService.getById(jobNumber);
    if (currentJobResult.error || !currentJobResult.data) {
      return { data: null, error: currentJobResult.error || new Error(`Job not found: ${jobNumber}`) };
    }
    // For migrated jobs, use materialsList as-is — the merge pipeline pulls in
    // project-config/bundle candidates that are not applicable to imported jobs.
    const baseMaterials = isExternalManagedImport(currentJobResult.data)
      ? (Array.isArray(currentJobResult.data.materialsList) ? currentJobResult.data.materialsList : [])
      : await buildMergedMaterialsList(currentJobResult.data);
    const mergedMaterials = resetReviewMaterialState(baseMaterials);

    await replaceJobMaterialsSnapshot({
      ...currentJobResult.data,
      materialsList: mergedMaterials,
    });

    const eventResult = await BuildReviewService.addEvent(jobNumber, "approved", performedBy, { notes });
    if (eventResult.error) return { data: null, error: eventResult.error };

    const updateResult = await JobService.update(jobNumber, {
      scopeLocked: true,
      intakeApproved: true,
      approvedForOrdering: true,
      planningStatus: "Ready for Ordering",
      orderStatus: "Not Ordered",
      procurementStatus: "Not Ordered",
      materialReadiness: "Not Ordered",
      currentStage: "Review Complete",
      materialsList: mergedMaterials,
    });

    if (updateResult.data) {
      await replaceJobMaterialsSnapshot({
        ...updateResult.data,
        materialsList: mergedMaterials,
      });
    }

    void SystemUpdateService.post({
      domain:   'jobs',
      event:    'build_review_approved',
      orgId:    currentJobResult.data.organizationId,
      body:     `System: Build Review Approved — ${jobNumber}`,
      metadata: {
        title:  'Build Review Approved',
        detail: `Job ${jobNumber}`,
        action: { label: 'View Build Review', path: `/jobs/${jobNumber}?tab=build-review` },
      },
    })

    return updateResult;
  },

  /**
   * Request a revision — puts the job on hold for the sales rep.
   */
  async requestRevision(
    jobNumber: string,
    performedBy: string,
    revisionNotes: string,
    revisionReason: RevisionReason,
    materialChanges?: string
  ): Promise<{ data: Job | null; error: unknown }> {
    const eventResult = await BuildReviewService.addEvent(
      jobNumber,
      "revision_requested",
      performedBy,
      { revisionNotes, revisionReason, materialChanges }
    );
    if (eventResult.error) return { data: null, error: eventResult.error };

    void SystemUpdateService.post({
      domain: 'jobs',
      event:  'build_review_revision',
      body:   `System: Build Review — Revision Requested for ${jobNumber}`,
    })

    return JobService.update(jobNumber, {
      holdStatus: "On Hold — Sales Action Required",
      holdReason: REVISION_REASON_LABELS[revisionReason],
    });
  },

  /**
   * Send the job to the sales rep for confirmation after revision.
   */
  async sendToSales(
    jobNumber: string,
    performedBy: string,
    notes?: string
  ): Promise<{ data: BuildReviewEvent | null; error: unknown }> {
    return BuildReviewService.addEvent(jobNumber, "sent_to_sales", performedBy, { notes });
  },

  /**
   * Sales rep confirms the revised scope.
   * Automatically creates a change order event.
   */
  async salesConfirm(
    jobNumber: string,
    performedBy: string,
    notes?: string
  ): Promise<{ data: BuildReviewEvent | null; error: unknown }> {
    const confirmedResult = await BuildReviewService.addEvent(jobNumber, "sales_confirmed", performedBy, { notes });
    if (confirmedResult.error) return { data: null, error: confirmedResult.error };

    return BuildReviewService.addEvent(
      jobNumber,
      "change_order_created",
      performedBy,
      { notes: notes ?? "Change order generated from scope revision" }
    );
  },

  /**
   * Sales rep resubmits after resolving the revision.
   */
  async salesResubmit(
    jobNumber: string,
    performedBy: string,
    notes?: string
  ): Promise<{ data: BuildReviewEvent | null; error: unknown }> {
    return BuildReviewService.addEvent(jobNumber, "sales_resubmitted", performedBy, { notes });
  },

  /**
   * Customer has signed the updated scope/change order.
   */
  async customerSigned(
    jobNumber: string,
    performedBy: string,
    notes?: string
  ): Promise<{ data: BuildReviewEvent | null; error: unknown }> {
    return BuildReviewService.addEvent(jobNumber, "customer_signed", performedBy, { notes });
  },

  /**
   * Final approval after customer signature — clears the job for material planning.
   */
  async finalApprove(
    jobNumber: string,
    performedBy: string,
    notes?: string
  ): Promise<{ data: Job | null; error: unknown }> {
    const currentJobResult = await JobService.getById(jobNumber);
    if (currentJobResult.error || !currentJobResult.data) {
      return { data: null, error: currentJobResult.error || new Error(`Job not found: ${jobNumber}`) };
    }
    // For migrated jobs, use materialsList as-is — same guard as approve().
    const baseMaterialsFinal = isExternalManagedImport(currentJobResult.data)
      ? (Array.isArray(currentJobResult.data.materialsList) ? currentJobResult.data.materialsList : [])
      : await buildMergedMaterialsList(currentJobResult.data);
    const mergedMaterials = resetReviewMaterialState(baseMaterialsFinal);

    await replaceJobMaterialsSnapshot({
      ...currentJobResult.data,
      materialsList: mergedMaterials,
    });

    const finalApprovedResult = await BuildReviewService.addEvent(jobNumber, "final_approved", performedBy, { notes });
    if (finalApprovedResult.error) return { data: null, error: finalApprovedResult.error };

    const approvedResult = await BuildReviewService.addEvent(jobNumber, "approved", performedBy, { notes });
    if (approvedResult.error) return { data: null, error: approvedResult.error };

    const updateResult = await JobService.update(jobNumber, {
      scopeLocked: true,
      intakeApproved: true,
      approvedForOrdering: true,
      planningStatus: "In Planning",
      currentStage: "Review Complete",
      holdStatus: undefined,
      holdReason: undefined,
      materialsList: mergedMaterials,
    });

    if (updateResult.data) {
      await replaceJobMaterialsSnapshot({
        ...updateResult.data,
        materialsList: mergedMaterials,
      });
    }

    void SystemUpdateService.post({
      domain:   'jobs',
      event:    'build_review_final_approved',
      orgId:    currentJobResult.data.organizationId,
      body:     `System: Build Review Final Approved — ${jobNumber}`,
      metadata: {
        title:  'Build Review Final Approved',
        detail: `Job ${jobNumber}`,
        action: { label: 'View Build Review', path: `/jobs/${jobNumber}?tab=build-review` },
      },
    })

    return updateResult;
  },

  /** Delete all review events for a job (used in testing / data cleanup) */
  async deleteByJobNumber(jobNumber: string): Promise<{ error: unknown }> {
    const jobUuid = await resolveJobUuid(jobNumber);
    if (!jobUuid) return { error: new Error(`Job not found: ${jobNumber}`) };

    const { error } = await supabase
      .from("build_review_events")
      .delete()
      .eq("job_id", jobUuid);
    return { error };
  },
};
